import { Link } from 'react-router-dom';
import {
  fetchSlides, fetchStats, fetchFeatures, fetchLevelCards,
  fetchNews, fetchEvents, fetchHomeWelcome, fetchHomeAdvantage,
} from '../lib/api.js';
import { useApi } from '../hooks/useApi.js';

import { Seo } from '../components/Seo.jsx';
import { HeroSlider } from '../components/HeroSlider.jsx';
import { StatsBar } from '../components/StatsBar.jsx';
import { SectionHead } from '../components/SectionHead.jsx';
import { FeatureGrid } from '../components/FeatureGrid.jsx';
import { CurriculumSlider } from '../components/CurriculumSlider.jsx';
import { SchoolSelector } from '../components/SchoolSelector.jsx';
import { JourneyBand } from '../components/JourneyBand.jsx';
import { LeadershipMessages } from '../components/LeadershipMessages.jsx';
import { PartnersStrip } from '../components/PartnersStrip.jsx';
import { NewsCard } from '../components/NewsCard.jsx';
import { HomePopup } from '../components/HomePopup.jsx';

// Fallback for the academics quick-links ribbon (used before Level cards load
// or if the API is unreachable). Mirrors the original hard-coded links.
const RIBBON_FALLBACK = [
  { ageRange: 'Ages 3–6', title: 'PG – KG', link: '/page/pg-kg' },
  { ageRange: 'Ages 6–11', title: 'Class I – V', link: '/page/i-v' },
  { ageRange: 'Ages 11–15', title: 'Class VI – IX', link: '/page/vi-ix' },
  { ageRange: 'Ages 14–16', title: 'IGCSE', link: '/page/igcse' },
  { ageRange: 'Ages 16–18', title: 'IAL', link: '/page/ial' },
];

export function HomePage() {
  const { data: slides } = useApi(fetchSlides, []);
  const { data: stats } = useApi(fetchStats, []);
  const { data: features } = useApi(fetchFeatures, []);
  const { data: levels } = useApi(fetchLevelCards, []);
  const { data: newsResponse } = useApi(() => fetchNews({ limit: 3 }), []);
  const { data: events } = useApi(() => fetchEvents({ upcoming: 'true' }), []);
  const { data: welcomeData } = useApi(fetchHomeWelcome, []);
  const { data: advantageData } = useApi(fetchHomeAdvantage, []);

  const news = newsResponse?.items || [];
  const upcomingEvents = (events || []).slice(0, 3);

  // Academics ribbon reads from the same Level cards the admin manages.
  const ribbonItems = (levels && levels.length) ? levels : RIBBON_FALLBACK;

  // "Discover Maple Leaf" welcome section — admin-managed via the Welcome
  // resource, with the original copy preserved as a fallback for first-paint /
  // API-down / not-yet-initialised scenarios.
  const welcome = welcomeData && typeof welcomeData === 'object'
    ? welcomeData
    : {
        eyebrow: 'Welcome',
        rawTitle: 'Discover <em>Maple Leaf</em>',
        body: 'Aspiring to be one of the great schools of South Asia, Maple Leaf International School offers a uniquely rewarding educational experience. Through our focus on thoughtful teaching and learning, students discover more, achieve more and become more.',
        primaryButtonLabel: 'About Us',
        primaryButtonUrl: '/about',
        secondaryButtonLabel: 'From the Principal',
        secondaryButtonUrl: '/page/principals-message',
      };

  // Section header for the "Why Maple Leaf" band — admin-managed via the
  // Advantage resource, with the original copy preserved as a safety-net
  // fallback for first-paint / API-down / not-yet-initialised scenarios.
  const advantage = advantageData && typeof advantageData === 'object'
    ? advantageData
    : {
        eyebrow: 'Why Maple Leaf',
        rawTitle: 'The Maple Leaf <em>Advantage</em>',
        lede: 'A thoughtful approach to teaching and a clear focus on academic excellence. A strong community ethos and a safe, supportive learning environment. Maple Leaf students have all they need to succeed.',
      };

  return (
    <>
      <HomePopup />
      <Seo
        title="Maple Leaf International School — Dhanmondi, Dhaka"
        description="One of Bangladesh's most prestigious English Medium schools — preparing students for the world's leading universities through the British Curriculum."
        image={slides?.[0]?.image}
      />

      {/* Hero slider */}
      <HeroSlider slides={slides} />

      {/* Academics quick-links ribbon */}
      <nav className="academics-ribbon" aria-label="Academic levels">
        <div className="container">
          {ribbonItems.map((lvl, i) => (
            <Link key={lvl.id ?? i} className="ribbon-link" to={lvl.link || '#'}>
              {lvl.ageRange && <span className="age">{lvl.ageRange}</span>}
              <span className="name">{lvl.title}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Stats bar */}
      <StatsBar stats={stats} />

      {/* Welcome / Discover Maple Leaf */}
      <section className="welcome">
        <div className="container welcome-grid">
          <div className="reveal">
            {welcome.eyebrow && <div className="section-eyebrow">{welcome.eyebrow}</div>}
            <h2 className="welcome-title" dangerouslySetInnerHTML={{ __html: welcome.rawTitle }} />
          </div>
          <div className="reveal">
            {welcome.body && <p className="welcome-lede">{welcome.body}</p>}
            <div className="welcome-actions">
              {welcome.primaryButtonLabel && (
                <Link to={welcome.primaryButtonUrl || '#'} className="btn btn-green">{welcome.primaryButtonLabel}</Link>
              )}
              {welcome.secondaryButtonLabel && (
                <Link to={welcome.secondaryButtonUrl || '#'} className="btn btn-ghost">{welcome.secondaryButtonLabel}</Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* The Maple Leaf Advantage — horizontal accordion */}
      <section className="advantage">
        <div className="container">
          <div className="advantage-intro reveal">
            <SectionHead
              eyebrow={advantage.eyebrow}
              rawTitle={advantage.rawTitle}
              lede={advantage.lede}
            />
          </div>
          <FeatureGrid features={features} />
        </div>
      </section>

      {/* Messages from leadership */}
      <LeadershipMessages />

      {/* Discover / Campus Selector */}
      <SchoolSelector />

      {/* Our Curriculum — homepage tile slider */}
      <CurriculumSlider items={levels} />

      {/* What's on — News & Events */}
      <section className="whatson">
        <div className="container">
          <div className="whatson-head reveal">
            <div>
              <div className="section-eyebrow">News &amp; Events</div>
              <h2 className="section-title">What's <em>on</em></h2>
            </div>
            <div className="whatson-controls">
              <Link to="/news" className="btn btn-ghost btn-sm">All News &amp; Events</Link>
            </div>
          </div>
          <div className="whatson-grid">
            {news.map((n) => <NewsCard key={n.id} item={n} variant="whatson" />)}
            {upcomingEvents.map((e) => (
              <NewsCard
                key={`event-${e.id}`}
                variant="whatson"
                item={{
                  slug: 'events',
                  category: 'event',
                  title: e.title,
                  heroImage: e.image,
                  publishedAt: e.eventDate,
                }}
              />
            ))}
            {news.length === 0 && upcomingEvents.length === 0 && (
              <p style={{ color: 'var(--ink-3)', gridColumn: '1 / -1' }}>
                No news or events to show yet — once content is added in the admin, it will appear here.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Affiliations & partners */}
      <PartnersStrip />

      {/* Start the journey — final CTA */}
      <JourneyBand />
    </>
  );
}
